import { Box, Card, CardBody, Flex, Stack, Text } from '@chakra-ui/react';
import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { sum } from 'lodash';
import React from 'react';

import {
    AccountConstraint,
    CashAllocation,
    CashExposureType,
    ConvictionAllocation,
    ExposureStatus,
    GrowthAllocation,
    SecuredAllocation,
} from '../_openapi/portfolio-optimizer';
import { AccountConstraintRow } from '../Allocation/AccountConstraintRow';
import {
    CashAllocationListColumns,
    ConvictionAllocationListColumns,
    GrowthAllocationListColumns,
    SecuredAllocationListColumns,
} from '../Allocation/AllocationListColumns';
import { useAccountAllocation } from '../api/allocation';
import { Table } from '../widgets/Table/Table';

type ColumnsConfig = {
    cash: SleeveConfig<Partial<CashAllocation>>;
    secured: SleeveConfig<Partial<SecuredAllocation>>;
    conviction: SleeveConfig<Partial<ConvictionAllocation & { exposureStatus: ExposureStatus }>>;
    growth: SleeveConfig<Partial<GrowthAllocation>>;
};

type SleeveConfig<T> = {
    label: string;
    columns: ColumnConfig<T>;
};

type ColumnConfig<T> = {
    [K in keyof T]: {
        includeInSubtotal: boolean;
        width?: number;
    };
};

const columnsConfig: ColumnsConfig = {
    cash: {
        label: 'Cash Sleeve',
        columns: {
            exposureType: { includeInSubtotal: false, width: 200 },
            targetExposure: { includeInSubtotal: true, width: 100 },
            currentExposure: { includeInSubtotal: true, width: 100 },
            activeExposure: { includeInSubtotal: true, width: 100 },
        },
    },
    secured: {
        label: 'Secured Sleeve',
        columns: {
            exposureType: { includeInSubtotal: false, width: 200 },
            targetExposure: { includeInSubtotal: true, width: 100 },
            currentExposure: { includeInSubtotal: true, width: 100 },
            activeExposure: { includeInSubtotal: true, width: 100 },
            exposureStatus: { includeInSubtotal: false },
            maxBuyConstraint: { includeInSubtotal: false, width: 100 },
            targetSecuredness: { includeInSubtotal: false, width: 100 },
            previousTargetSecuredness: { includeInSubtotal: false, width: 100 },
            initialSecuredness: { includeInSubtotal: false, width: 100 },
            targetDuration: { includeInSubtotal: false, width: 100 },
            initialDuration: { includeInSubtotal: false, width: 100 },
            targetFundedness: { includeInSubtotal: false, width: 100 },
            initialFundedness: { includeInSubtotal: false, width: 100 },
        },
    },
    conviction: {
        label: 'Conviction Sleeve',
        columns: {
            allocationType: { includeInSubtotal: false, width: 200 },
            targetExposure: { includeInSubtotal: true, width: 100 },
            currentExposure: { includeInSubtotal: true, width: 100 },
            activeExposure: { includeInSubtotal: true, width: 100 },
            exposureStatus: { includeInSubtotal: false },
            maxBuyConstraint: { includeInSubtotal: false, width: 100 },
            maxTargetExposure: { includeInSubtotal: false },
        },
    },
    growth: {
        label: 'Growth Sleeve',
        columns: {
            allocationType: { includeInSubtotal: false, width: 200 },
            exposureStatus: { includeInSubtotal: false },
            targetExposure: { includeInSubtotal: true, width: 100 },
            currentExposure: { includeInSubtotal: true, width: 100 },
            activeExposure: { includeInSubtotal: true, width: 100 },
            targetWeight: { includeInSubtotal: false, width: 100 },
            currentWeight: { includeInSubtotal: false, width: 100 },
            activeWeight: { includeInSubtotal: false, width: 100 },
        },
    },
};

const getExposureStatusBackground = (originalCell: any) => {
    const statusValue = originalCell?.props?.value?.toLowerCase();

    switch (statusValue) {
        case ExposureStatus.OVERWEIGHT:
            return 'red.800';
        case ExposureStatus.UNDERWEIGHT:
            return 'blue.600';
        case ExposureStatus.ON_TARGET:
            return undefined;
        default:
            return undefined;
    }
};

const getStyledColumn = (col: any, columnType: keyof typeof columnsConfig, labelColumnId: string) => {
    const selectedColumnsConfig = columnsConfig[columnType];
    const columnConfig = col.id
        ? selectedColumnsConfig.columns[col.id as keyof typeof selectedColumnsConfig.columns]
        : undefined;

    return {
        ...col,
        ...(columnConfig?.width && { size: columnConfig.width }),
        cell: (props: any) => {
            const originalCell = typeof col.cell === 'function' ? col.cell(props) : props.getValue();

            // Add color and font styling for sub total row
            if (props.row.original.isSubtotal) {
                const subtotalColumnsConfig = columnsConfig[columnType];

                if (
                    col.id &&
                    subtotalColumnsConfig.columns[col.id as keyof typeof subtotalColumnsConfig.columns]
                        ?.includeInSubtotal
                ) {
                    return (
                        <Box fontWeight="bold" color="teal.300">
                            {originalCell}
                        </Box>
                    );
                }
                return (
                    <Box fontWeight="bold" color="teal.300">
                        {col.id === labelColumnId ? subtotalColumnsConfig.label : ''}
                    </Box>
                );
            }

            // Add background color styling for exposureStatus column
            if (col.id === 'exposureStatus') {
                const backgroundColor = getExposureStatusBackground(originalCell);
                return (
                    <Box
                        width="100%"
                        height="100%"
                        bg={backgroundColor}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {originalCell}
                    </Box>
                );
            }

            return originalCell;
        },
    };
};

const cashAllocationColumnsConfig = CashAllocationListColumns.filter(
    (col) => col.id && col.id in columnsConfig.cash.columns,
).map((col) => getStyledColumn(col, 'cash', 'exposureType'));

const securedAllocationColumnsConfig = SecuredAllocationListColumns.filter(
    (col) => col.id && col.id in columnsConfig.secured.columns,
).map((col) => getStyledColumn(col, 'secured', 'exposureType'));

const convictionAllocationColumnsConfig = ConvictionAllocationListColumns.filter(
    (col) => col.id && col.id in columnsConfig.conviction.columns,
).map((col) => getStyledColumn(col, 'conviction', 'allocationType'));

const growthAllocationColumnsConfig = GrowthAllocationListColumns.filter(
    (col) => col.id && col.id in columnsConfig.growth.columns,
).map((col) => getStyledColumn(col, 'growth', 'allocationType'));

const TableCard = ({
    title,
    data,
    columns,
    isLoading,
    header,
}: {
    title: string;
    data: any[];
    columns: ColumnDef<any>[];
    isLoading: boolean;
    header?: React.ReactNode;
}) => {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        enableSorting: false,
        enableColumnFilters: false,
    });

    return (
        <Stack gap={1}>
            <Text fontSize="lg" fontWeight="bold">
                {title}
            </Text>
            <Card width="fit-content" maxWidth="100%">
                <CardBody>
                    {header}
                    <Table table={table} isLoading={isLoading} allowXOverflow shrinkToContent />
                </CardBody>
            </Card>
        </Stack>
    );
};

export interface AllocationTablesData {
    accountConstraint?: AccountConstraint | null;
    cashAllocation: CashAllocation[];
    securedAllocation: SecuredAllocation[];
    convictionAllocation: ConvictionAllocation[];
    growthAllocation: GrowthAllocation[];
}

export const AllocationTablesView = ({
    allocation,
    isLoading = false,
    cashAllocationAside,
}: {
    allocation: AllocationTablesData;
    isLoading?: boolean;
    cashAllocationAside?: React.ReactNode;
}) => {
    const growthAllocationDataWithSubtotals = React.useMemo(() => {
        const data = allocation.growthAllocation ?? [];
        if (data.length === 0) return data;

        const sortedData = [...data].sort((a, b) => {
            const aWeight = a.targetWeight || 0;
            const bWeight = b.targetWeight || 0;
            if (aWeight !== bWeight) return bWeight - aWeight;
            return (a.allocationType || '').localeCompare(b.allocationType || '');
        });

        const subtotal = {
            isSubtotal: true,
            targetExposure: sum(data.map((row) => row.targetExposure || 0)),
            currentExposure: sum(data.map((row) => row.currentExposure || 0)),
            activeExposure: sum(data.map((row) => row.activeExposure || 0)),
        };

        return [...sortedData, subtotal];
    }, [allocation.growthAllocation]);

    const securedAllocationDataWithSubtotals = React.useMemo(() => {
        const data = allocation.securedAllocation ?? [];
        if (data.length === 0) return data;

        const subtotal = {
            isSubtotal: true,
            targetExposure: sum(data.map((row) => row.targetExposure || 0)),
            currentExposure: sum(data.map((row) => row.currentExposure || 0)),
            activeExposure: sum(data.map((row) => row.activeExposure || 0)),
        };

        return [...data, subtotal];
    }, [allocation.securedAllocation]);

    const convictionAllocationDataWithSubtotals = React.useMemo(() => {
        const data = allocation.convictionAllocation ?? [];
        if (data.length === 0) return data;

        const subtotal = {
            isSubtotal: true,
            targetExposure: sum(data.map((row) => row.targetExposure ?? 0)),
            currentExposure: sum(data.map((row) => row.currentExposure ?? 0)),
            activeExposure: sum(data.map((row) => row.activeExposure ?? 0)),
        };

        return [...data, subtotal];
    }, [allocation.convictionAllocation]);

    const cashAllocationDataWithSubtotals = React.useMemo(() => {
        const data = allocation.cashAllocation ?? [];
        if (data.length === 0) return data;

        const orderMap: { [key in CashExposureType]: number } = {
            [CashExposureType.FEE]: 1,
            [CashExposureType.AD_HOC_WITHDRAWAL]: 2,
            [CashExposureType.PLANNED_WITHDRAWAL]: 2,
            [CashExposureType.BUFFER]: 3,
            [CashExposureType.UNENCUMBERED]: 4,
        };

        const sortedData = [...data].sort(
            (a, b) => (orderMap[a.exposureType] || 999) - (orderMap[b.exposureType] || 999),
        );

        const subtotal = {
            isSubtotal: true,
            targetExposure: sum(data.map((row) => row.targetExposure || 0)),
            currentExposure: sum(data.map((row) => row.currentExposure || 0)),
            activeExposure: sum(data.map((row) => row.activeExposure || 0)),
        };

        return [...sortedData, subtotal];
    }, [allocation.cashAllocation]);

    return (
        <Stack gap={4} width="100%">
            <Flex gap={4} align="flex-start" wrap="wrap">
                <TableCard
                    title="Cash Allocation"
                    data={cashAllocationDataWithSubtotals}
                    columns={cashAllocationColumnsConfig}
                    isLoading={isLoading}
                />
                {cashAllocationAside && (
                    <Box flex={1} minW="480px">
                        {cashAllocationAside}
                    </Box>
                )}
            </Flex>
            {allocation.securedAllocation.length > 0 && (
                <TableCard
                    title="Secured Allocation"
                    data={securedAllocationDataWithSubtotals}
                    columns={securedAllocationColumnsConfig}
                    isLoading={isLoading}
                />
            )}
            {allocation.convictionAllocation.length > 0 && (
                <TableCard
                    title="Conviction Allocation"
                    data={convictionAllocationDataWithSubtotals}
                    columns={convictionAllocationColumnsConfig}
                    isLoading={isLoading}
                />
            )}
            <TableCard
                title="Growth Allocation"
                data={growthAllocationDataWithSubtotals}
                columns={growthAllocationColumnsConfig}
                isLoading={isLoading}
                header={<AccountConstraintRow accountConstraint={allocation.accountConstraint} />}
            />
        </Stack>
    );
};

export const PortfolioAllocationTables = ({ portfolioGuid }: { portfolioGuid?: string }) => {
    const { data: allocation, isPending: isAllocationPending } = useAccountAllocation(portfolioGuid);

    if (!allocation) return null;

    return <AllocationTablesView allocation={allocation} isLoading={isAllocationPending} />;
};
